import { PropsWithChildren, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IconButton, Stack, StackProps, Typography } from '@mui/material';

import { IcArrowLeft } from '@/assets/images';
import useShouldHideNavButton from '@/hooks/useShouldHideNavButton';
import { getNewTabSearch, isOpenedInNewTab } from '@/utils';

import { PageHeaderRoot } from './styles';

interface PageHeaderProps extends PropsWithChildren, Omit<StackProps, 'title'> {
  backRoute?: string;
  title?: ReactNode | string;
  onBack?: () => void;
  action?: ReactNode;
  forceHideBackButton?: boolean;
}

export const PageHeader = (props: PageHeaderProps) => {
  const {
    backRoute,
    title,
    children,
    onBack,
    forceHideBackButton = false,
    action,
    ...rest
  } = props;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hideNavButton = useShouldHideNavButton();
  const isNewTab = isOpenedInNewTab(searchParams);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backRoute) {
      const search = isNewTab ? getNewTabSearch(searchParams) : '';
      navigate(`${backRoute}${search}`);
      localStorage.removeItem('cfs-back-route');
    } else {
      navigate(-1); // This uses browser history to go back
    }
  };

  return (
    <PageHeaderRoot {...rest}>
      <Stack className="header-breadcrumb">
        {(!hideNavButton || !!onBack) && !forceHideBackButton && (
          <IconButton className="btnBack" onClick={handleBack}>
            <IcArrowLeft />
          </IconButton>
        )}
        {!!title && (
          <Typography component="div" className="page-title">
            {title}
          </Typography>
        )}
      </Stack>
      <Stack
        flex={1}
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        columnGap={2}
      >
        {children}
        {action}
      </Stack>
    </PageHeaderRoot>
  );
};
